import { useState, useEffect } from 'react';
import { useApolloClient } from '@apollo/client';
import { GET_CONTRACTS_FOR_LIST } from '@/graphql/queries/getContractsForList';
import { contractFilterInputFromDashboard } from '@/helpers/dashboardContractFilter';
import {
  PageSizeOption,
  type DashboardFilterInput,
  type GetContractsForListQuery,
  type GetContractsForListQueryVariables,
} from '@/graphql/generated';

type ContractItem = GetContractsForListQuery['contracts']['items'][number];

type UseAllContractsForDashboardResult = {
  contracts: ContractItem[];
  loading: boolean;
  error: Error | null;
};

type UseAllContractsForDashboardOptions = {
  dashboardFilter?: DashboardFilterInput;
};

export const useAllContractsForDashboard = (
  options?: UseAllContractsForDashboardOptions,
): UseAllContractsForDashboardResult => {
  const client = useApolloClient();
  const dashboardFilter = options?.dashboardFilter;
  const [contracts, setContracts] = useState<ContractItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    const filterSlice = contractFilterInputFromDashboard(dashboardFilter);

    const fetchAll = async () => {
      setLoading(true);
      setError(null);
      const allItems: ContractItem[] = [];
      let page = 1;
      let hasNext = true;

      try {
        while (hasNext && !cancelled) {
          const { data } = await client.query<
            GetContractsForListQuery,
            GetContractsForListQueryVariables
          >({
            query: GET_CONTRACTS_FOR_LIST,
            variables: {
              input: {
                pageSize: PageSizeOption.OneHundred,
                page: page.toString(),
                ...filterSlice,
              },
            },
            fetchPolicy: 'network-only',
          });

          const items = data?.contracts?.items ?? [];
          const pagination = data?.contracts?.pagination;

          allItems.push(...items);

          hasNext = pagination?.hasNextPage ?? false;
          page += 1;
        }

        if (!cancelled) {
          setContracts(allItems);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e : new Error(String(e)));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void fetchAll();
    return () => {
      cancelled = true;
    };
  }, [client, dashboardFilter]);

  return { contracts, loading, error };
};
